// In the desktop app the screen is opened in a window whose title bar is
// hidden, so the page's top row reaches the window's top edge. On mac the
// traffic lights sit over its left end; on Windows and Linux the screen draws
// minimize / maximize / close itself at its right end. The window bridge says
// where the lights sit and hands over the buttons; this turns that into a
// title-bar source for the screen (the shape ui-core's `MeExtensions.titleBar`
// takes). The bridge is there before the screen's first script runs, so the
// first paint already leaves the ends free. No node APIs: screens bundle this.
import type {
  TitleBarInset,
  TrameriaDesktopBridge,
  WindowControls,
} from "./window-bridge";

/** The ends the window's buttons cover, in CSS px from the window's edges. */
export interface TitleBarArea {
  left: number;
  right: number;
  height: number;
}

/** Where the ends are now and when that changes (`get` is null in full screen). */
export interface TitleBarAreaSource {
  get(): TitleBarArea | null;
  subscribe(onChange: () => void): () => void;
  /** Shows or hides the traffic lights (the screen shows them only with its sidebar open). */
  setControlsVisible?(visible: boolean): void;
  /** Minimize / maximize / close for the screen to draw (not on mac). */
  windowControls?: WindowControls;
}

/** What tells the source that the page was resized (the window, in a screen). */
export type ResizeTarget = Pick<
  EventTarget,
  "addEventListener" | "removeEventListener"
>;

/** The page zoom (⌘+ / ⌘−); an impossible value counts as 100%. */
function zoomOf(bridge: Pick<TrameriaDesktopBridge, "zoomFactor">): number {
  const zoom = bridge.zoomFactor();
  return Number.isFinite(zoom) && zoom >= 0.25 && zoom <= 5 ? zoom : 1;
}

const positive = (n: number) => Number.isFinite(n) && n > 0;

/**
 * The covered ends in CSS px: the bridge gives window coordinates, so they are
 * divided by the zoom. None in full screen (the buttons go away).
 */
export function titleBarArea(
  inset: TitleBarInset,
  fullScreen: boolean,
  zoom: number,
): TitleBarArea | null {
  if (fullScreen) return null;
  const { insetLeft, insetRight, height } = inset;
  if (!positive(height)) return null;
  return {
    left: positive(insetLeft) ? Math.ceil(insetLeft / zoom) : 0,
    right: positive(insetRight) ? Math.ceil(insetRight / zoom) : 0,
    height: Math.ceil(height / zoom),
  };
}

/**
 * The screen's title-bar source in the desktop app; undefined in a browser and
 * where the window keeps its title bar (no `titleBar` on the bridge). Follows
 * full screen and the zoom (a zoom change resizes the page in CSS px).
 */
export function desktopTitleBar(
  bridge: TrameriaDesktopBridge | undefined,
  resized: ResizeTarget = window,
): TitleBarAreaSource | undefined {
  const inset = bridge?.titleBar;
  if (!bridge || !inset) return undefined;
  return {
    get: () => titleBarArea(inset, bridge.isFullScreen(), zoomOf(bridge)),
    ...(bridge.setWindowButtonsVisible
      ? {
          setControlsVisible: (visible: boolean) =>
            bridge.setWindowButtonsVisible?.(visible),
        }
      : {}),
    ...(bridge.windowControls ? { windowControls: bridge.windowControls } : {}),
    subscribe: (onChange) => {
      const off = bridge.onFullScreenChange(() => onChange());
      resized.addEventListener("resize", onChange);
      return () => {
        off();
        resized.removeEventListener("resize", onChange);
      };
    },
  };
}
